import { useState, useMemo } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ChevronLeft, ChevronRight, CalendarDays,
  Video, FileText, Shield, Folder, Trophy,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useCurrentBatch, ALL_BATCHES } from '@/lib/hooks/useCurrentBatch'
import { useHome } from '@/lib/hooks/useHome'
import { useSchedule, useUpcoming } from '@/lib/hooks/useLms'
import type { ScheduleItem, UpcomingItem } from '@/lib/api/lms'

// ─── Event type config ────────────────────────────────────────────────────────

type CalEventType = 'live_session' | 'assignment' | 'assessment' | 'milestone' | 'event'

const EVENT_META: Record<CalEventType, {
  label: string
  dotColor: string
  dotSquare: boolean
  iconBg: string
  iconColor: string
  Icon: React.ElementType
}> = {
  live_session: {
    label: 'Live Session',
    dotColor: '#6366f1',
    dotSquare: false,
    iconBg: '#ede9fe',
    iconColor: '#6366f1',
    Icon: Video,
  },
  assignment: {
    label: 'Assignment Due',
    dotColor: '#f97316',
    dotSquare: false,
    iconBg: '#fff7ed',
    iconColor: '#f97316',
    Icon: FileText,
  },
  assessment: {
    label: 'Assessment',
    dotColor: '#22c55e',
    dotSquare: true,
    iconBg: '#dcfce7',
    iconColor: '#16a34a',
    Icon: Shield,
  },
  milestone: {
    label: 'Project Milestone',
    dotColor: '#7c3aed',
    dotSquare: true,
    iconBg: '#ede9fe',
    iconColor: '#7c3aed',
    Icon: Folder,
  },
  event: {
    label: 'Contest / Hackathon',
    dotColor: '#f59e0b',
    dotSquare: true,
    iconBg: '#fffbeb',
    iconColor: '#d97706',
    Icon: Trophy,
  },
}

/** Everything the calendar places on a day. Always carries a time. */
type DatedItem = ScheduleItem

function toCalEventType(item: DatedItem): CalEventType {
  if (item.kind === 'contest' || item.kind === 'hackathon') return 'event'
  if (item.kind === 'session') return 'live_session'
  if (item.category === 'projects') return 'milestone'
  if (item.category === 'assignments') return 'assignment'
  return 'assessment' // problems + quizzes
}

/** Same mapping for the undated practice list, which still comes from /upcoming. */
function undatedCalEventType(type: string): CalEventType {
  if (type === 'mcq_quiz' || type === 'coding_challenge' || type === 'lab') return 'assessment'
  if (type === 'project_assignment') return 'assignment'
  return 'live_session'
}

// A calendar row is styled by state as well as kind: work you finished reads
// quietly, work you missed doesn't.
const STATE_STYLE: Record<ScheduleItem['state'], { row: string; badge: string; label?: string }> = {
  upcoming: { row: '', badge: '' },
  live: { row: 'ring-1 ring-[#f97316]/40', badge: 'bg-[#fff4f0] text-[#f97316]', label: 'Live now' },
  attended: { row: 'opacity-70', badge: 'bg-[#f0fdf4] text-[#16a34a]', label: 'Attended' },
  // Held, but nobody marked the register — say so rather than guessing.
  done: { row: 'opacity-60', badge: 'bg-[#f3f4f6] text-[#6b7280]', label: 'Done' },
  missed: {
    row: 'border-[#fecaca] bg-[#fef7f7]',
    badge: 'bg-[#fef2f2] text-[#dc2626]',
    label: 'Missed',
  },
}

/** Submitted work reads as "Done"; a class that merely happened reads as "Held". */
function stateLabel(item: DatedItem): string | undefined {
  const style = STATE_STYLE[item.state]
  if (item.state === 'done' && item.kind === 'session') return 'Held'
  return style.label
}

/**
 * Where a calendar row navigates. Routed off the item's own batch so "All
 * Courses" doesn't send you into the wrong course.
 */
function scheduleHref(item: DatedItem): string | null {
  if (item.kind === 'contest') return '/contests'
  if (item.kind === 'hackathon') return `/hackathons/${item.id}`
  if (!item.batch_id) return null
  if (item.kind === 'session') {
    return item.topic_id ? `/courses/${item.batch_id}/topics/${item.topic_id}` : null
  }
  return item.category
    ? `/courses/${item.batch_id}/practice/${item.category}/${item.id}`
    : null
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function fmtEventTime(iso: string, type: CalEventType, compact = false): string {
  if (type === 'assignment' || type === 'milestone') {
    return compact ? `Due ${fmtTime(iso)}` : `Due by ${fmtTime(iso)}`
  }
  if (type === 'assessment') {
    return compact ? `Opens ${fmtTime(iso)}` : `Opens at ${fmtTime(iso)}`
  }
  if (compact) return fmtTime(iso)
  const start = new Date(iso)
  const end = new Date(start.getTime() + 90 * 60 * 1000)
  const fmt = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `${fmt(start)} – ${fmt(end)}`
}

function cleanTitle(title: string) {
  return title.replace(/^Session:\s*/i, '')
}

// Groups sessions that share the same time slot (same minute) into one display entry
function groupByTime(sessions: DatedItem[]): Array<{ primary: DatedItem; all: DatedItem[] }> {
  const map = new Map<string, DatedItem[]>()
  for (const s of sessions) {
    const key = new Date(s.at).toISOString().slice(0, 16)
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(s)
  }
  return Array.from(map.values())
    .sort((a, b) => new Date(a[0].at).getTime() - new Date(b[0].at).getTime())
    .map(items => ({ primary: items[0], all: items }))
}

// ─── Dot ─────────────────────────────────────────────────────────────────────

function Dot({ type }: { type: CalEventType }) {
  const { dotColor, dotSquare } = EVENT_META[type]
  return (
    <span
      className={cn('inline-block h-1 w-1', dotSquare ? 'rounded-[1px]' : 'rounded-full')}
      style={{ backgroundColor: dotColor }}
    />
  )
}

// ─── Calendar Grid ────────────────────────────────────────────────────────────

interface CalEvent {
  date: Date
  type: CalEventType
  session: DatedItem
}

interface GridProps {
  events: CalEvent[]
  selectedDate: Date
  onSelect: (d: Date) => void
  month: Date
  onPrev: () => void
  onNext: () => void
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DAY_TOOLTIP_MAX = 6

function CalGrid({ events, selectedDate, onSelect, month, onPrev, onNext }: GridProps) {
  const todayStr = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.toDateString()
  }, [])

  const evMap = useMemo(() => {
    const m = new Map<string, Set<CalEventType>>()
    for (const e of events) {
      const k = e.date.toDateString()
      if (!m.has(k)) m.set(k, new Set())
      m.get(k)!.add(e.type)
    }
    return m
  }, [events])

  // Distinct topic titles scheduled that day, for the hover tooltip — dedup
  // by title since a grouped live session can otherwise repeat itself.
  const titlesMap = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const e of events) {
      const k = e.date.toDateString()
      const title = cleanTitle(e.session.title)
      const list = m.get(k) ?? []
      if (!list.includes(title)) list.push(title)
      m.set(k, list)
    }
    return m
  }, [events])

  const cells = useMemo(() => {
    const y = month.getFullYear()
    const mo = month.getMonth()
    const startDow = new Date(y, mo, 1).getDay()
    const daysInMonth = new Date(y, mo + 1, 0).getDate()
    const result: { date: Date; outside: boolean }[] = []
    for (let i = startDow - 1; i >= 0; i--) result.push({ date: new Date(y, mo, -i), outside: true })
    for (let d = 1; d <= daysInMonth; d++) result.push({ date: new Date(y, mo, d), outside: false })
    const rem = 42 - result.length
    for (let d = 1; d <= rem; d++) result.push({ date: new Date(y, mo + 1, d), outside: true })
    return result
  }, [month])

  const monthLabel = month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  const selStr = selectedDate.toDateString()

  return (
    <div className="rounded-2xl border border-[#ececec] bg-white p-6 shadow-[0px_1px_3px_rgba(0,0,0,0.06)]">
      {/* Month nav */}
      <div className="mb-3 flex items-center justify-between">
        <button
          onClick={onPrev}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[#9ca3af] transition-colors hover:bg-[#f5f5f5]"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold text-[#111827]">{monthLabel}</span>
        <button
          onClick={onNext}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-[#9ca3af] transition-colors hover:bg-[#f5f5f5]"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-0.5">
        {WEEKDAYS.map(d => (
          <p key={d} className="py-1 text-center text-xs font-medium text-[#9ca3af]">{d}</p>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7">
        {cells.map((cell, i) => {
          const key = cell.date.toDateString()
          const types = [...(evMap.get(key) ?? [])]
          const titles = titlesMap.get(key) ?? []
          const isSelected = key === selStr
          const isToday = key === todayStr && !cell.outside

          const dayButton = (
            <button
              onClick={() => !cell.outside && onSelect(cell.date)}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-xl text-sm font-medium transition-colors',
                isSelected && 'bg-[#6366f1] text-white shadow-sm',
                !isSelected && isToday && 'font-semibold text-[#6366f1]',
                !isSelected && !isToday && !cell.outside && 'text-[#111827] hover:bg-[#f5f5f5]',
                cell.outside && 'cursor-default text-[#d1d5db]',
              )}
            >
              {cell.date.getDate()}
            </button>
          )

          return (
            <div key={i} className="flex flex-col items-center gap-0.5 py-0.5">
              {titles.length > 0 && !cell.outside ? (
                <Tooltip>
                  <TooltipTrigger asChild>{dayButton}</TooltipTrigger>
                  <TooltipContent className="max-w-[240px]">
                    <p className="space-y-0.5">
                      {titles.slice(0, DAY_TOOLTIP_MAX).map((t, j) => (
                        <span key={j} className="block">{t}</span>
                      ))}
                      {titles.length > DAY_TOOLTIP_MAX && (
                        <span className="block opacity-70">+{titles.length - DAY_TOOLTIP_MAX} more</span>
                      )}
                    </p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                dayButton
              )}
              {types.length > 0 && !cell.outside && (
                <div className="flex h-1.5 items-center gap-0.5">
                  {types.slice(0, 4).map((t, j) => (
                    <Dot key={j} type={t} />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-[#f5f5f5] pt-3">
        {(Object.keys(EVENT_META) as CalEventType[]).map(type => (
          <div key={type} className="flex items-center gap-1.5">
            <Dot type={type} />
            <span className="text-xs text-[#6b7280]">{EVENT_META[type].label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Day Detail Panel ─────────────────────────────────────────────────────────

function DayPanel({ date, sessions }: { date: Date; sessions: DatedItem[] }) {
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase()
  const dateLabel = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })

  return (
    <div className="rounded-2xl border border-[#ececec] bg-white p-6 shadow-[0px_1px_3px_rgba(0,0,0,0.06)]">
      <div className="mb-3">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-[#9ca3af]">{weekday}</p>
        <h2 className="mt-0.5 text-2xl font-bold text-[#111827]">{dateLabel}</h2>
      </div>

      {sessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f5f5f5]">
            <CalendarDays className="h-6 w-6 text-[#d1d5db]" />
          </div>
          <p className="text-sm text-[#9ca3af]">No events on this day</p>
        </div>
      ) : (
        <>
          {sessions.length > 1 && (
            <p className="mb-3 text-xs font-medium text-[#9ca3af]">
              {sessions.length} sessions scheduled
            </p>
          )}
          <div className="max-h-[360px] space-y-3 overflow-y-auto pr-0.5">
          {groupByTime(sessions).map(({ primary: s, all }) => {
            const evType = toCalEventType(s)
            const meta = EVENT_META[evType]
            const Icon = meta.Icon
            const isMulti = all.length > 1
            const displayTitle = isMulti ? `${all.length} Topics Covered` : cleanTitle(s.title)
            const displaySub = isMulti
              ? all.map(x => cleanTitle(x.title)).join(' · ')
              : s.topic_title || s.batch_name || ''
            const state = STATE_STYLE[s.state]
            const href = scheduleHref(s)
            const RowTag = (href ? Link : 'div') as React.ElementType
            return (
              <RowTag
                key={s.id}
                {...(href ? { to: href } : {})}
                className={cn(
                  'flex items-start gap-3 rounded-xl border border-[#f0f0f0] p-3.5 transition-colors',
                  href && 'hover:border-[#6366f1] hover:bg-[#fafaff]',
                  state.row,
                )}
              >
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                  style={{ backgroundColor: meta.iconBg }}
                >
                  <Icon className="h-5 w-5" style={{ color: meta.iconColor }} />
                </div>
                <div className="min-w-0 flex-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="truncate text-sm font-semibold text-[#111827]">{displayTitle}</p>
                    </TooltipTrigger>
                    <TooltipContent>{displayTitle}</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="mt-0.5 truncate text-xs text-[#9ca3af]">{displaySub}</p>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[260px]">{displaySub}</TooltipContent>
                  </Tooltip>
                  <p className="mt-1 text-xs font-medium" style={{ color: meta.iconColor }}>
                    {fmtEventTime(s.at, evType)}
                  </p>
                </div>
                {stateLabel(s) ? (
                  <span
                    className={cn(
                      'mt-0.5 shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
                      state.badge,
                    )}
                  >
                    {stateLabel(s)}
                  </span>
                ) : (
                  <span
                    className="mt-0.5 shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
                    style={{ backgroundColor: meta.iconBg, color: meta.iconColor }}
                  >
                    {meta.label}
                  </span>
                )}
              </RowTag>
            )
          })}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Upcoming This Week ───────────────────────────────────────────────────────

function UpcomingWeek({ sessions }: { sessions: DatedItem[] }) {
  const [showAll, setShowAll] = useState(false)

  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  const days = useMemo(() => {
    const dateMap = new Map<string, { date: Date; label: string; isToday: boolean; sessions: DatedItem[] }>()
    for (const s of sessions) {
      const d = new Date(s.at)
      d.setHours(0, 0, 0, 0)
      const key = d.toDateString()
      if (d < today) continue
      const diffDays = Math.round((d.getTime() - today.getTime()) / 86400000)
      if (!showAll && diffDays >= 7) continue
      if (!dateMap.has(key)) {
        const isToday = key === today.toDateString()
        dateMap.set(key, {
          date: new Date(d),
          label: isToday ? 'Today' : d.toLocaleDateString('en-US', { weekday: 'short' }),
          isToday,
          sessions: [],
        })
      }
      dateMap.get(key)!.sessions.push(s)
    }
    return Array.from(dateMap.values()).sort((a, b) => a.date.getTime() - b.date.getTime())
  }, [sessions, today, showAll])

  if (days.length === 0) return null

  return (
    <div className="rounded-2xl border border-[#ececec] bg-white p-6 shadow-[0px_1px_3px_rgba(0,0,0,0.06)]">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-bold text-[#111827]">
          {showAll ? 'All Upcoming Sessions' : 'Upcoming This Week'}
        </h2>
        <button
          onClick={() => setShowAll(v => !v)}
          className="text-sm font-semibold text-[#6366f1] transition-opacity hover:opacity-70"
        >
          {showAll ? 'Show Less' : 'View All'}
        </button>
      </div>

      <div className="flex divide-x divide-[#f0f0f0] overflow-x-auto scrollbar-none">
        {days.map((day, i) => (
          <div key={i} className="min-w-[160px] flex-1 px-4 first:pl-0 last:pr-0">
            <p className={cn('text-xs font-bold', day.isToday ? 'text-[#f97316]' : 'text-[#9ca3af]')}>
              {day.label}
            </p>
            <p className="mt-0.5 text-sm font-bold text-[#111827]">
              {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </p>
            <div className="mt-3 space-y-2.5">
              {groupByTime(day.sessions).slice(0, 3).map(({ primary: s, all }) => {
                const evType = toCalEventType(s)
                const meta = EVENT_META[evType]
                const Icon = meta.Icon
                const isMulti = all.length > 1
                const label = isMulti ? `${all.length} Topics` : cleanTitle(s.title)
                const fullLabel = isMulti ? all.map(x => cleanTitle(x.title)).join(', ') : cleanTitle(s.title)
                const href = scheduleHref(s)
                const RowTag = (href ? Link : 'div') as React.ElementType
                return (
                  <RowTag
                    key={s.id}
                    {...(href ? { to: href } : {})}
                    className="group flex items-start gap-2"
                  >
                    <div
                      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
                      style={{ backgroundColor: meta.iconBg }}
                    >
                      <Icon className="h-3.5 w-3.5" style={{ color: meta.iconColor }} />
                    </div>
                    <div className="min-w-0">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="truncate text-xs font-semibold text-[#111827] transition-colors group-hover:text-[#6366f1]">
                            {label}
                          </p>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[220px]">{fullLabel}</TooltipContent>
                      </Tooltip>
                      <p className="text-[11px] text-[#9ca3af]">{fmtEventTime(s.at, evType, true)}</p>
                    </div>
                  </RowTag>
                )
              })}
              {groupByTime(day.sessions).length > 3 && (
                <p className="text-[11px] font-medium text-[#9ca3af]">
                  +{groupByTime(day.sessions).length - 3} more
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Practice, No Due Date Yet ────────────────────────────────────────────────
// Assessments/assignments a trainer hasn't scheduled a due date for don't
// belong on a specific calendar day — surfacing them here instead of
// stamping them onto "today" keeps that false urgency out of the calendar.

function UndatedPractice({ items }: { items: UpcomingItem[] }) {
  const [showAll, setShowAll] = useState(false)
  if (items.length === 0) return null

  const visible = showAll ? items : items.slice(0, 6)

  return (
    <div className="rounded-2xl border border-[#ececec] bg-white p-6 shadow-[0px_1px_3px_rgba(0,0,0,0.06)]">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-[#111827]">Practice — No Due Date Yet</h2>
          <p className="mt-0.5 text-xs text-[#9ca3af]">{items.length} item{items.length === 1 ? '' : 's'} your trainer hasn't scheduled a deadline for</p>
        </div>
        {items.length > 6 && (
          <button
            onClick={() => setShowAll(v => !v)}
            className="shrink-0 text-sm font-semibold text-[#6366f1] transition-opacity hover:opacity-70"
          >
            {showAll ? 'Show Less' : `View All ${items.length}`}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {visible.map(s => {
          const evType = undatedCalEventType(s.type)
          const meta = EVENT_META[evType]
          const Icon = meta.Icon
          return (
            <Link
              key={s.id}
              to="/courses/$batchId/topics/$topicId"
              params={{ batchId: s.batch_id, topicId: s.topic_id }}
              className="group flex items-center gap-2.5 rounded-xl border border-[#f0f0f0] p-2.5 transition-colors hover:border-[#6366f1] hover:bg-[#fafaff]"
            >
              <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                style={{ backgroundColor: meta.iconBg }}
              >
                <Icon className="h-4 w-4" style={{ color: meta.iconColor }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-[#111827] transition-colors group-hover:text-[#6366f1]">
                  {cleanTitle(s.title)}
                </p>
                <p className="truncate text-[11px] text-[#9ca3af]">{s.topic_title}</p>
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function SchedulePage() {
  const { batchId, isAll, batches } = useCurrentBatch()
  const homeBatchId = isAll ? ALL_BATCHES : (batchId ?? undefined)
  const { data: homeData } = useHome(homeBatchId)

  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  })
  const [displayMonth, setDisplayMonth] = useState<Date>(() => {
    const d = new Date()
    d.setDate(1)
    d.setHours(0, 0, 0, 0)
    return d
  })

  const upcomingBatchId = isAll ? 'all' : (batchId ?? '')

  // Fetch the displayed month plus a month either side, so paging back and
  // forth doesn't blank the grid while the next window loads.
  const [from, to] = useMemo(() => {
    const y = displayMonth.getFullYear()
    const m = displayMonth.getMonth()
    return [
      new Date(y, m - 1, 1).toISOString(),
      new Date(y, m + 2, 1).toISOString(),
    ]
  }, [displayMonth])

  const { data: scheduleItems } = useSchedule(upcomingBatchId, from, to)
  // /upcoming is still the source for practice with no deadline set — those
  // items have no date, so they can't live on the calendar.
  const { data: sessions } = useUpcoming(upcomingBatchId)
  const batchName = homeData?.batch?.name ?? batches[0]?.name ?? ''

  const datedSessions = useMemo(() => scheduleItems ?? [], [scheduleItems])
  const undatedSessions = useMemo(
    () => (sessions ?? []).filter(s => !s.scheduled_at),
    [sessions],
  )

  const calEvents: CalEvent[] = useMemo(
    () =>
      datedSessions.map(s => ({
        date: new Date(s.at),
        type: toCalEventType(s),
        session: s,
      })),
    [datedSessions],
  )

  const daySessions = useMemo(() => {
    const str = selectedDate.toDateString()
    return datedSessions.filter(s => new Date(s.at).toDateString() === str)
  }, [datedSessions, selectedDate])

  if (!batchId && !isAll) return null

  return (
    <TooltipProvider delayDuration={300}>
    <div className="mx-auto w-full max-w-[1150px] space-y-8 px-4 py-6 sm:px-10 sm:py-10">
      {/* Page header */}
      <div className="min-w-0">
        <nav className="flex items-center gap-1.5 text-xs" aria-label="Breadcrumb">
          <Link to="/courses" className="text-[#9ca3af] transition-colors hover:text-[#6366f1]">My Batches</Link>
          {batchName && !isAll && (
            <>
              <ChevronRight className="size-3 text-[#9ca3af]" />
              <Link
                to="/courses/$batchId"
                params={{ batchId: batchId! }}
                className="text-[#f97316] transition-opacity hover:opacity-80"
              >
                {batchName}
              </Link>
            </>
          )}
          <ChevronRight className="size-3 text-[#9ca3af]" />
          <span className="font-medium text-[#6366f1]">Calendar</span>
        </nav>
        <h1 className="pt-3 text-[34px] font-bold leading-[1.05] tracking-tight text-[#111827] sm:text-[52px] sm:leading-[52px]">
          Calendar
        </h1>
        <p className="pt-3 text-sm text-[#9ca3af]">Sessions, deadlines and assessments.</p>
      </div>

      {/* Calendar + Day panel */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_400px]">
        <CalGrid
          events={calEvents}
          selectedDate={selectedDate}
          onSelect={setSelectedDate}
          month={displayMonth}
          onPrev={() => setDisplayMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
          onNext={() => setDisplayMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
        />
        <DayPanel date={selectedDate} sessions={daySessions} />
      </div>

      {/* Upcoming this week */}
      <UpcomingWeek sessions={datedSessions} />

      {/* Practice with no due date set */}
      <UndatedPractice items={undatedSessions} />
    </div>
    </TooltipProvider>
  )
}
